import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { videoApi } from '@carrent/api'
import { useAuthStore } from '@carrent/core'
import { PageHeader, Icon, EventTime } from '@carrent/ui'
import {
  CALL_PRIVACIES,
  CALL_STATUSES,
  isJoinable,
  useVideoCalls,
  useVideoSettings,
} from '@carrent/domain'

/**
 * Видеозвонки: список и создание.
 *
 * Список живёт на событиях шины (video.call.*): «идёт» загорается
 * без перезагрузки. Создание — панель на этой же странице, а не
 * отдельный маршрут: форма из четырёх полей не заслуживает экрана.
 */

const STATUS_FILTERS = [
  { value: '', label: 'Все' },
  { value: 'active', label: 'Идут' },
  { value: 'scheduled', label: 'Запланированы' },
  { value: 'ended', label: 'Прошедшие' },
]

function CreateCallForm({ settings, onCreated, onCancel }) {
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [privacy, setPrivacy] = useState(settings.default_privacy || 'password')
  const [password, setPassword] = useState('')
  const [ttl, setTtl] = useState(settings.default_ttl_hours || 4)
  const [when, setWhen] = useState('now')          // now | scheduled
  const [scheduledAt, setScheduledAt] = useState('')
  const [q, setQ] = useState('')
  const [found, setFound] = useState([])
  const [selected, setSelected] = useState([])     // сотрудники
  const [guestNames, setGuestNames] = useState('') // внешние гости
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  // @-упоминание: ведущий @ — часть синтаксиса, для поиска его срезаем,
  // чтобы «@Иван» и «Иван» находили одно и то же.
  const query = q.startsWith('@') ? q.slice(1) : q

  // Поиск сотрудников через video/staff-search — доступен и менеджеру,
  // и отдаёт только одобренных (в отличие от admin/users).
  useEffect(() => {
    if (query.trim().length < 1) {
      setFound([])
      return undefined
    }
    let alive = true
    const t = setTimeout(() => {
      videoApi.staffSearch(query.trim())
        .then(({ data }) => alive && setFound(data.data || []))
        .catch(() => alive && setFound([]))
    }, 250)
    return () => {
      alive = false
      clearTimeout(t)
    }
  }, [query])

  const add = (u) => {
    if (!selected.some((s) => s.id === u.id)) setSelected([...selected, u])
    setQ('')
    setFound([])
  }

  const submit = async () => {
    setSaving(true)
    setError('')
    try {
      const payload = { title: title.trim(), privacy, ttl_hours: ttl }
      if (description.trim()) payload.description = description.trim()
      if (privacy === 'password') payload.password = password
      if (when === 'scheduled' && scheduledAt) {
        payload.scheduled_at = new Date(scheduledAt).toISOString()
      }
      if (selected.length) payload.participant_ids = selected.map((u) => u.id)
      const { data } = await videoApi.create(payload)
      const call = data.data

      // Гостевые ссылки создаются сразу и показываются ОДИН раз —
      // передаём их на страницу звонка через state навигации.
      let invites = null
      const guests = guestNames
        .split(/[,\n]/)
        .map((n) => n.trim())
        .filter(Boolean)
        .map((name) => ({ name }))
      if (guests.length) {
        const res = await videoApi.invites(call.id, guests)
        invites = res.data.data.invites
      }
      onCreated(call, invites)
    } catch (e) {
      setError(e.response?.data?.message || 'Не удалось создать звонок')
      setSaving(false)
    }
  }

  const invalid = !title.trim()
    || (privacy === 'password' && password.length < 4)
    || (when === 'scheduled' && !scheduledAt)

  return (
    <section className="card flex-col gap">
      <h2 className="section-title">Новый звонок</h2>

      <div className="field">
        <label className="field__label" htmlFor="call-title">Название</label>
        <input
          id="call-title"
          className="input"
          value={title}
          maxLength={200}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Планёрка по региону, видеоосмотр…"
        />
      </div>

      <div className="field">
        <label className="field__label" htmlFor="call-desc">Описание / повестка</label>
        <textarea
          id="call-desc"
          className="input"
          rows={2}
          value={description}
          maxLength={2000}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="О чём встреча, вопросы к обсуждению (необязательно)"
        />
      </div>

      <div className="flex-row gap-sm video-actions">
        <button
          type="button"
          className={`btn btn--sm ${when === 'now' ? 'btn--primary' : 'btn--outline'}`}
          onClick={() => setWhen('now')}
        >
          Начать сейчас
        </button>
        <button
          type="button"
          className={`btn btn--sm ${when === 'scheduled' ? 'btn--primary' : 'btn--outline'}`}
          onClick={() => setWhen('scheduled')}
        >
          Запланировать
        </button>
      </div>

      {when === 'scheduled' && (
        <div className="field">
          <label className="field__label" htmlFor="call-when">Дата и время начала</label>
          <input
            id="call-when"
            className="input"
            type="datetime-local"
            value={scheduledAt}
            onChange={(e) => setScheduledAt(e.target.value)}
          />
          {!scheduledAt && (
            <span className="field__hint">Укажите, когда начнётся звонок.</span>
          )}
        </div>
      )}

      <div className="flex-col gap-sm">
        <span className="field__label">Приватность</span>
        {Object.entries(CALL_PRIVACIES).map(([value, o]) => (
          <label className="settings__row" key={value}>
            <span className="settings__text">
              <span className="settings__label">{o.label}</span>
              <span className="settings__hint">{o.hint}</span>
            </span>
            <input
              type="radio"
              name="call-privacy"
              checked={privacy === value}
              onChange={() => setPrivacy(value)}
            />
          </label>
        ))}
      </div>

      {privacy === 'password' && (
        <div className="field">
          <label className="field__label" htmlFor="call-password">Пароль</label>
          <div className="flex-row gap-sm">
            <input
              id="call-password"
              className="input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Минимум 4 символа"
            />
            <button
              type="button"
              className="btn btn--outline"
              onClick={() => setPassword(Math.random().toString(36).slice(2, 10))}
            >
              Сгенерировать
            </button>
          </div>
        </div>
      )}

      <div className="field">
        <label className="field__label" htmlFor="call-staff">Пригласить сотрудников</label>
        <input
          id="call-staff"
          className="input"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Введите @ или имя для поиска"
        />
        {found.map((u) => (
          <button
            key={u.id}
            type="button"
            className="chats__candidate"
            onClick={() => add(u)}
          >
            <Icon name="plus" size={14} /> {u.full_name} · {u.phone}
          </button>
        ))}
        {selected.length > 0 && (
          <div className="chats__chips">
            {selected.map((u) => (
              <span key={u.id} className="chats__chip">
                {u.full_name}
                <button
                  type="button"
                  aria-label={`Убрать ${u.full_name}`}
                  onClick={() => setSelected(selected.filter((x) => x.id !== u.id))}
                >
                  <Icon name="close" size={12} />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      {settings.guest_access && (
        <div className="field">
          <label className="field__label" htmlFor="call-guests">Внешние гости</label>
          <textarea
            id="call-guests"
            className="input"
            rows={2}
            value={guestNames}
            onChange={(e) => setGuestNames(e.target.value)}
            placeholder="Имена через запятую: Страховой комиссар, Арендодатель"
          />
          <span className="field__hint">
            Персональные ссылки будут показаны один раз после создания.
          </span>
        </div>
      )}

      <div className="field">
        <label className="field__label" htmlFor="call-ttl">Срок действия ссылки, часов</label>
        <input
          id="call-ttl"
          className="input video-form-narrow"
          type="number"
          min={1}
          max={168}
          value={ttl}
          onChange={(e) => setTtl(Math.min(168, Math.max(1, parseInt(e.target.value, 10) || 1)))}
        />
        <span className="field__hint">
          У запланированного звонка срок считается от времени начала.
        </span>
      </div>

      {error && <div className="alert alert--error">{error}</div>}

      <div className="flex-row gap-sm">
        <button
          type="button"
          className="btn btn--primary"
          onClick={submit}
          disabled={saving || invalid}
        >
          {saving ? <span className="spinner spinner--sm" /> : <Icon name="check" size={14} />}
          Создать
        </button>
        <button type="button" className="btn btn--ghost" onClick={onCancel} disabled={saving}>
          Отменить
        </button>
      </div>
    </section>
  )
}

export default function VideoCallsPage() {
  const { user } = useAuthStore()
  const { settings, canCreate } = useVideoSettings()
  const [status, setStatus] = useState('')
  const [creating, setCreating] = useState(false)
  const navigate = useNavigate()
  const { items, loading, error, refresh } = useVideoCalls({ status: status || undefined })

  const allowCreate = useMemo(() => canCreate(user?.role), [canCreate, user])

  if (!settings.enabled) {
    return (
      <div className="p-page flex-col gap">
        <PageHeader title="Видеозвонки" />
        <div className="state">
          <Icon name="settings" size={32} />
          <p className="state__title">Модуль выключен</p>
          <p className="state__text">
            Видеозвонки включает администратор в разделе «Настройки».
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="p-page flex-col gap">
      <PageHeader
        title="Видеозвонки"
        right={
          allowCreate && !creating ? (
            <button
              type="button"
              className="btn btn--primary btn--sm"
              onClick={() => setCreating(true)}
            >
              <Icon name="plus" size={14} /> Новый звонок
            </button>
          ) : undefined
        }
      />

      {creating && (
        <CreateCallForm
          settings={settings}
          onCancel={() => setCreating(false)}
          onCreated={(call, invites) => {
            setCreating(false)
            navigate(`/admin/video/${call.id}`, invites ? { state: { invites } } : undefined)
          }}
        />
      )}

      <div className="flex-row gap-sm video-actions">
        {STATUS_FILTERS.map((f) => (
          <button
            key={f.value}
            type="button"
            className={`btn btn--sm ${status === f.value ? 'btn--primary' : 'btn--outline'}`}
            onClick={() => setStatus(f.value)}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading && (
        <div className="card flex-col gap-sm">
          <span className="skeleton table__skeleton-row" />
          <span className="skeleton table__skeleton-row" />
        </div>
      )}

      {!loading && error && (
        <div className="state">
          <p className="state__title">Не удалось загрузить звонки</p>
          <button type="button" className="btn btn--secondary" onClick={refresh}>
            Повторить
          </button>
        </div>
      )}

      {!loading && !error && items.length === 0 && (
        <div className="state">
          <Icon name="chat" size={32} />
          <p className="state__title">Звонков пока нет</p>
        </div>
      )}

      {!loading && items.length > 0 && (
        <div className="flex-col gap-sm">
          {items.map((call) => {
            const st = CALL_STATUSES[call.status] || { label: call.status }
            return (
              <Link
                key={call.id}
                to={`/admin/video/${call.id}`}
                className="card flex-row video-list-item"
              >
                <div className="flex-col video-list-item__body">
                  <span className="video-list-item__title">{call.title}</span>
                  <span className="text-muted video-hint">
                    {CALL_PRIVACIES[call.privacy]?.label}
                    {' · создан '}<EventTime value={call.created_at} />
                    {call.created_by ? ` · ${call.created_by.full_name}` : ''}
                  </span>
                </div>
                <span className={`badge ${call.status === 'active' ? 'badge--success' : ''}`}>
                  {st.label}
                </span>
                {isJoinable(call) && <Icon name="chevron-right" size={16} />}
              </Link>
            )
          })}
        </div>
      )}
    </div>
  )
}
